/**
 * Automation rules, escalation, the scheduled jobs and the Slack webhook.
 *
 * A rule fires once per client per cooldown window; the sweep checks them
 * every few minutes while the workspace is open.
 */
import { useApp } from '../../store/AppContext.jsx';
import { TRIGGERS } from '../../constants/defaults.js';
import Icon from '../../lib/icons.jsx';
import { Switch } from '../../components/Primitives.jsx';
import { postSlack, slackSummary } from '../../services/automation.js';
import { Section, Grid, Row, NumberField, TextField, CheckGrid, Toggle } from './SettingsBits.jsx';

function Rule({ rule }) {
  const { S, updateSettings } = useApp();

  const patch = partial => updateSettings(next => {
    next.rules = next.rules.map(r => (r.id === rule.id ? { ...r, ...partial } : r));
  });

  const remove = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the rule “${rule.name}”?`)) return;
    updateSettings(next => { next.rules = next.rules.filter(r => r.id !== rule.id); });
  };

  const ownerOptions = ['', '__rr', ...(S.owners || [])];

  return (
    <div className={`rule${rule.enabled ? '' : ' off'}`}>
      <div className="rh">
        <Switch on={rule.enabled} onClick={() => patch({ enabled: !rule.enabled })} title="Enable or disable" />
        <input type="text" value={rule.name} onChange={e => patch({ name: e.target.value })} />
        <button type="button" className="btn sm danger" onClick={remove}><Icon name="trash" /></button>
      </div>

      <div className="rgrid">
        <div>
          <label>When</label>
          <select value={rule.on} onChange={e => patch({ on: e.target.value })}>
            {TRIGGERS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </select>
        </div>

        <div>
          <label>Assign to</label>
          <select value={rule.assign} onChange={e => patch({ assign: e.target.value })}>
            {ownerOptions.map(o => (
              <option key={o || '_none'} value={o}>
                {o === '' ? '— leave as is' : o === '__rr' ? 'Round-robin (next owner)' : o}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Set status</label>
          <select value={rule.status} onChange={e => patch({ status: e.target.value })}>
            <option value="">— leave as is</option>
            {(S.statuses || []).map(x => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>

        <div>
          <label>Channel</label>
          <select value={rule.channel || 'email'} onChange={e => patch({ channel: e.target.value })}>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </div>

        <div>
          <label>Repeat after</label>
          <input
            type="number"
            min="0"
            max="365"
            value={rule.cooldown ?? 7}
            onChange={e => patch({ cooldown: parseInt(e.target.value, 10) || 0 })}
          />
        </div>

        <div>
          <label>Note to add</label>
          <input
            type="text"
            placeholder="optional"
            value={rule.note || ''}
            onChange={e => patch({ note: e.target.value })}
          />
        </div>
      </div>

      <div className="rflags">
        <label>
          <input type="checkbox" checked={!!rule.draft} onChange={e => patch({ draft: e.target.checked })} />
          Draft a message into the outbox
        </label>
        <label>
          <input type="checkbox" checked={!!rule.notify} onChange={e => patch({ notify: e.target.checked })} />
          Send a notification
        </label>
      </div>
    </div>
  );
}

export default function AutomationPane() {
  const { S, updateSettings, runRules, alerts, mrr, atRisk, toast } = useApp();

  const addRule = () => updateSettings(next => {
    next.rules = [...(next.rules || []), {
      id: `r${Date.now()}`, name: 'New rule', on: 'unpaid', assign: '', status: '',
      channel: 'email', cooldown: 7, note: '', draft: true, notify: true, enabled: false
    }];
  });

  const testSlack = async () => {
    try {
      await postSlack(S, slackSummary(S, alerts, mrr, atRisk));
      toast('Sent to Slack');
    } catch (e) {
      toast(e.message || 'Slack rejected the request — use Copy for Slack instead');
    }
  };

  const copySlack = () => {
    const text = slackSummary(S, alerts, mrr, atRisk);
    if (!navigator.clipboard) { toast('Clipboard unavailable'); return; }
    navigator.clipboard.writeText(text).then(() => toast('Copied for Slack'), () => toast('Copy failed'));
  };

  return (
    <>
      <Section
        title="Rules"
        hint="Checked on every sweep. A rule fires once per client, then waits for the repeat window."
      >
        {(S.rules || []).length
          ? (S.rules || []).map(rule => <Rule key={rule.id} rule={rule} />)
          : <p className="muted">No rules yet.</p>}
        <Row>
          <button type="button" className="btn" onClick={addRule}><Icon name="plus" />Add rule</button>
          <button type="button" className="btn" onClick={() => { runRules({ announce: false }); toast('Rules run'); }}>
            Run rules now
          </button>
        </Row>
      </Section>

      <Section
        title="Recurring billing"
        hint="How far ahead a due payment is flagged, and what a monthly cycle does when paid."
      >
        <Grid>
          <NumberField
            label="Flag payments due within"
            name="dueSoonDays"
            unit="days before the due date"
            min={1}
            max={60}
          />
        </Grid>
        <p style={{ marginTop: 8 }}>
          Marking a client paid records the amount, stamps today, advances the due date by one cycle
          and sets them Active.
        </p>
      </Section>

      <Section title="Escalation" hint="Alerts nobody has opened get bumped to urgent and re-notified.">
        <Grid>
          <NumberField
            label="Escalate after"
            name="escalateHours"
            unit="hours untouched · 0 turns it off"
            min={0}
            max={336}
          />
        </Grid>
      </Section>

      <Section title="Scheduled jobs" hint="These run in the background while the workspace is open.">
        <CheckGrid>
          <Toggle label="Daily team digest" name="digestOn" />
          <Toggle label="Nightly AI risk scan" name="autoRisk" />
          <Toggle label="Morning brief" name="autoBrief" />
          <Toggle label="Post the digest to Slack" name="digestSlack" />
        </CheckGrid>
        <div className="g2" style={{ marginTop: 11 }}>
          <NumberField label="Digest at" name="digestHour" unit="hour, 24h clock" min={0} max={23} />
          <NumberField label="Risk scan at" name="riskHour" unit="hour, 24h clock" min={0} max={23} />
          <TextField label="Digest recipients" name="digestTo" placeholder="team@company.ae" />
        </div>
      </Section>

      <Section
        title="Slack"
        hint="Paste an incoming-webhook URL. Browsers cannot always post to Slack directly — if it fails, use “Copy for Slack”."
      >
        <TextField name="slackWebhook" placeholder="https://hooks.slack.com/services/…" />
        <Row>
          <button type="button" className="btn" onClick={testSlack}>Send a test</button>
          <button type="button" className="btn" onClick={copySlack}>Copy for Slack</button>
        </Row>
      </Section>
    </>
  );
}
